import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../PublicLayout';
import { CheckCircle, ArrowRight } from 'lucide-react';

const ProgramsTemplate = ({ 
  title, 
  subtitle, 
  description, 
  icon: Icon, 
  features, 
  impact, 
  programs, 
  ctaText = 'Get Involved',
  ctaLink = '/get-involved/become-member'
}) => {
  return (
    <PublicLayout>
      <div className="py-5" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4" 
                style={{ width: '80px', height: '80px', backgroundColor: '#6B8E23' }}>
                {Icon && <Icon className="text-white" size={40} />}
              </div>
              <h1 className="display-4 fw-bold text-dark mb-3">{title}</h1>
              <p className="lead text-muted">{subtitle}</p>
            </div>
          </div>

          {/* Description */}
          <div className="row justify-content-center mb-5">
            <div className="col-lg-10">
              <div className="card shadow border-0">
                <div className="card-body p-4 p-lg-5">
                  <div className="text-muted">
                    {description}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features/Benefits */}
          {features && features.length > 0 && (
            <div className="row justify-content-center mb-5">
              <div className="col-lg-10">
                <h2 className="h3 fw-bold text-dark mb-4 text-center">Key Features</h2>
                <div className="row g-4">
                  {features.map((feature, index) => (
                    <div key={index} className="col-md-6">
                      <div className="card h-100 shadow-sm border-0 hover-shadow" style={{ transition: 'all 0.3s' }}>
                        <div className="card-body p-4">
                          <div className="d-flex align-items-start">
                            <CheckCircle className="text-success me-3 flex-shrink-0 mt-1" size={24} style={{ color: '#6B8E23' }} />
                            <div>
                              <h3 className="h5 fw-bold text-dark mb-2">{feature.title}</h3>
                              <p className="text-muted mb-0">{feature.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Programs/Initiatives */}
          {programs && programs.length > 0 && (
            <div className="row justify-content-center mb-5">
              <div className="col-lg-10">
                <h2 className="h3 fw-bold text-dark mb-4 text-center">Our Programs</h2>
                <div className="d-flex flex-column gap-3">
                  {programs.map((program, index) => (
                    <div key={index} className="card shadow-sm border-0 hover-shadow" style={{ transition: 'all 0.3s' }}>
                      <div className="card-body p-4">
                        <h3 className="h5 fw-bold text-dark mb-2">{program.name}</h3>
                        <p className="text-muted mb-3">{program.description}</p>
                        {program.link && (
                          <Link to={program.link} className="fw-semibold d-inline-flex align-items-center text-decoration-none" 
                            style={{ color: '#6B8E23' }}>
                            Learn More <ArrowRight size={16} className="ms-1" />
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Impact Statistics */}
          {impact && impact.length > 0 && (
            <div className="row justify-content-center mb-5">
              <div className="col-lg-10">
                <h2 className="h3 fw-bold text-dark mb-4 text-center">Our Impact</h2>
                <div className="row g-4">
                  {impact.map((stat, index) => (
                    <div key={index} className="col-md-4">
                      <div className="card text-white shadow h-100" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
                        <div className="card-body p-4 text-center">
                          <h3 className="display-4 fw-bold mb-2">{stat.value}</h3>
                          <p className="mb-0" style={{ color: '#e8f5e9' }}>{stat.label}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card text-white shadow-lg border-0" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
                <div className="card-body p-5 text-center">
                  <h2 className="h3 fw-bold mb-3">Join Us in This Mission</h2>
                  <p className="lead mb-4">Be part of transforming respiratory care across Africa</p>
                  <Link 
                    to={ctaLink} 
                    className="btn btn-light btn-lg fw-bold shadow"
                    style={{ color: '#6B8E23' }}
                  >
                    {ctaText}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ProgramsTemplate;
